import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@pulpo/app-config';
import {
  DailyReport,
  DailyReporting,
  RequestReportAxis,
  RequestReportCrossFilters,
  StepReport,
  StepReportContent,
  UserRequestFilter,
  UserRequestLine,
  UserRequestStat,
  UserRequestStatScope,
} from '@pulpo/pulpo-models';
import ApiService from '../api.service.abstract';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface SpringPage<T> {
  content: T[];
  totalElements?: number;
  page?: { totalElements: number };
}

interface AxisReportResponse {
  report: DailyReporting;
  productNames?: { [key: string]: string };
  supplierNames?: { [key: string]: string };
  destinationNames?: { [key: string]: string };
}

@Injectable({
  providedIn: 'root',
})
export class StepReportService extends ApiService<StepReport> {
  private readonly AXIS_ENDPOINTS: Record<
    RequestReportAxis,
    { path: string; param: string }
  > = {
    product: { path: 'producer/products', param: 'productReference' },
    canal: { path: 'producer/canals', param: 'canal' },
    supplier: { path: 'producer/suppliers', param: 'supplierId' },
    destination: { path: 'producer/destinations', param: 'destinationId' },
  };

  private readonly AXIS_NAMES_KEY: Record<
    RequestReportAxis,
    keyof Omit<AxisReportResponse, 'report'> | undefined
  > = {
    product: 'productNames',
    canal: undefined,
    supplier: 'supplierNames',
    destination: 'destinationNames',
  };

  constructor(
    @Inject(APP_CONFIG) environment: any,
    protected http: HttpClient
  ) {
    super(environment, http, 'booking/api/step-reports');
    if (environment.name === 'localDev') {
      this.url = 'http://localhost:8086';
    }
  }

  getAxisReport(
    axis: RequestReportAxis,
    values: (string | number)[] | undefined,
    days: number,
    crossFilters?: RequestReportCrossFilters
  ): Observable<DailyReporting> {
    const { path, param } = this.AXIS_ENDPOINTS[axis];
    let options: HttpParams = this.createRequestOption({ days });
    for (const value of values || []) {
      options = options.append(param, value);
    }
    // Cross filters narrow the report by dimensions other than the current
    // axis (e.g. Destination/Distributeur/Canal on the Produits tab). The UI
    // never sends the filter matching the current axis here - that one is
    // already covered by `values`/`param` above - so there's no risk of the
    // same query param being appended twice.
    for (const value of crossFilters?.destinationIds || []) {
      options = options.append('destinationId', value);
    }
    for (const value of crossFilters?.supplierIds || []) {
      options = options.append('supplierId', value);
    }
    for (const value of crossFilters?.canals || []) {
      options = options.append('canal', value);
    }
    return this.http
      .get<AxisReportResponse>(`${this.url}/${this.endpoint}/${path}`, {
        params: options,
      })
      .pipe(
        map((body) => {
          const namesKey = this.AXIS_NAMES_KEY[axis];
          return {
            ...body.report,
            names: namesKey ? body[namesKey] : undefined,
          };
        })
      );
  }

  getWeekReport(): Observable<HttpResponse<{ [key: string]: DailyReport }[]>> {
    return this.http.get<{ [key: string]: DailyReport }[]>(
      `${this.url}/${this.endpoint}/daily`,
      { observe: 'response' }
    );
  }

  getContent(id: string): Observable<HttpResponse<StepReportContent>> {
    return this.http.get<StepReportContent>(
      `${this.url}/${this.endpoint}/content/${id}`,
      { observe: 'response' }
    );
  }

  getUserRequestStats(
    scope: UserRequestStatScope,
    days: number
  ): Observable<UserRequestStat[]> {
    return this.http.get<UserRequestStat[]>(
      `${this.url}/${this.endpoint}/${this.userStatsPath(scope)}`,
      { params: this.createRequestOption({ days }) }
    );
  }

  getUserRequests(
    scope: UserRequestStatScope,
    filter: UserRequestFilter,
    page: number,
    size: number
  ): Observable<{ content: UserRequestLine[]; totalElements: number }> {
    const params = this.createRequestOption(
      Object.fromEntries(
        Object.entries({ ...filter, page, size }).filter(
          ([, value]) => value !== undefined && value !== null && value !== ''
        )
      )
    );
    return this.http
      .get<SpringPage<UserRequestLine>>(
        `${this.url}/${this.endpoint}/${this.userStatsPath(scope)}/requests`,
        { params }
      )
      .pipe(
        map((body) => ({
          content: body.content || [],
          totalElements: body.page?.totalElements ?? body.totalElements ?? 0,
        }))
      );
  }

  private userStatsPath(scope: UserRequestStatScope): string {
    return scope === 'admin' ? 'users' : 'producer/users';
  }
}
